import { useEffect, useRef } from 'react';

interface AudioPlaybackProps {
  src: string | null;
}

// VI-368: audio preview — centered waveform graphic with a dark bottom gradient
// and the browser's NATIVE audio controls (users get play / seek / volume for free).
//
// VI-702: MediaRecorder WebM/Opus blobs omit the duration header, so the browser
// can report duration=Infinity — the native control then shows "0:00 /" with no
// total and a broken seek bar. Same priming VideoPlayback uses: after metadata
// loads, if duration isn't finite, seek past the end so the browser scans to the
// real end, resolving the duration and making the blob seekable. We snap the
// playhead back to 0 on `seeked` UNCONDITIONALLY so it is never stranded at the
// far right — even when the duration can't be resolved (the earlier bug).
const AudioPlayback = ({ src }: AudioPlaybackProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !src) return;

    let primed = false;

    const tryFix = () => {
      if (primed || isFinite(a.duration)) return;
      try { a.currentTime = 1e101; } catch { /* not seekable yet */ }
    };

    const onSeeked = () => {
      // Runs after the seek-past-end completes. Snap back to the start regardless
      // of whether the duration resolved, so the playhead never sticks at the end.
      if (!primed) {
        primed = true;
        try { a.currentTime = 0; } catch { /* ignore */ }
      }
    };

    a.addEventListener('loadedmetadata', tryFix);
    a.addEventListener('seeked', onSeeked);
    tryFix(); // in case metadata already loaded

    return () => {
      a.removeEventListener('loadedmetadata', tryFix);
      a.removeEventListener('seeked', onSeeked);
    };
  }, [src]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', backgroundColor: 'transparent', overflow: 'hidden' }}>
      {/* Waveform graphic, centered */}
      <div
        style={{
          width: '80%',
          height: '50%',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <img src="/svgs/waveform-gray.svg" alt="Audio waveform" style={{ width: '100%', height: '100%' }} />
      </div>

      {/* Dark gradient at the bottom with the browser's native audio controls */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '70%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0) 100%)',
          display: 'flex',
          alignItems: 'flex-end',
          padding: '0 20px 20px 20px',
        }}
      >
        <audio
          ref={audioRef}
          src={src || undefined}
          controls
          preload="metadata"
          style={{ width: '100%', display: 'block' }}
        />
      </div>
    </div>
  );
};

export default AudioPlayback;
